// Brand-canonical chart helpers — typed wrappers around Chart.js.
//
// `drawSortableStacked` is the standard pattern for any "X by category, broken
// down by series" chart in Bounteous internal tools: stacked horizontal bars
// where bars re-sort by sum-of-visible-series whenever the user toggles a
// legend item. Tooltip lists each visible segment + a footer "Total: $X" line.
//
// Reference implementation:
//   ~/.claude/skills/bounteous-branding/reference/sortable-stacked-chart.js
//   /Users/premkalyan/code/BODQ22026/board-portal/ai-explorer.html (lines 753–810)

import { fmtMoney } from "../lib/format";

// SGNL-130: bundled via npm instead of CDN (eliminates external dep + CDN
// availability risk; improves CSP posture; works offline in local dev).
import { Chart } from "chart.js/auto";

type ChartHandle = { destroy(): void; update(): void };

interface ChartGlobal {
  new (
    ctx: HTMLCanvasElement,
    cfg: Record<string, unknown>,
  ): ChartHandle & {
    data: { labels: string[]; datasets: { data: number[]; hidden?: boolean }[] };
  };
}

const chartRegistry = new Map<string, ChartHandle>();

export interface SortableSeries<R> {
  label: string;
  /** key on each row whose number value contributes to this series */
  key: keyof R;
  /** brand color (hex). Use the canonical mapping per the bounteous-branding skill. */
  color: string;
}

export interface SortableStackedOptions {
  /**
   * SGNL-109 — Bar orientation. "y" = horizontal bars (category on Y, $ on X)
   * is the legacy BODQ22026 default. "x" = vertical bars (category on X,
   * $ on Y) — used by the AI breakdown tab where ranking-at-a-glance reads
   * better top-to-bottom.
   */
  indexAxis?: "x" | "y";
}

/**
 * Render (or re-render) a Chart.js stacked-bar chart in the given canvas.
 * Bars are sorted by sum-of-visible-series; toggling a legend item re-sorts.
 *
 * - `rows`: aggregated data (one entry per category, e.g. one per BU)
 * - `labelKey`: key on each row that holds the category label
 * - `series`: list of stack components (one dataset each)
 * - `opts.indexAxis`: "y" (default, horizontal) or "x" (vertical).
 *
 * Money tick formatting uses `fmtMoney` so $K/$M/$B abbreviation is consistent.
 */
export function drawSortableStacked<R>(
  canvasId: string,
  rows: R[],
  labelKey: keyof R,
  series: SortableSeries<R>[],
  title: string,
  opts: SortableStackedOptions = {},
): void {
  const indexAxis = opts.indexAxis ?? "y";
  const moneyAxis: "x" | "y" = indexAxis === "y" ? "x" : "y";
  const ctx = document.getElementById(canvasId);
  if (!(ctx instanceof HTMLCanvasElement)) return;

  // Destroy any existing chart bound to this canvas — re-renders are common
  // when filters change.
  chartRegistry.get(canvasId)?.destroy();

  const visible = new Set<number>(series.map((_, i) => i));

  // Coerce to a finite number — Chart.js sorting + rendering misbehave on
  // NaN/Infinity, and a single bad value would silently corrupt the chart.
  // Per PR-Agent finding on PR #10.
  const num = (v: unknown): number => {
    const n = Number(v ?? 0);
    return Number.isFinite(n) ? n : 0;
  };
  const rebuild = (visibleSet: Set<number>) => {
    const sorted = rows.slice().sort((a, b) => {
      let sa = 0;
      let sb = 0;
      series.forEach((s, i) => {
        if (visibleSet.has(i)) {
          sa += num(a[s.key]);
          sb += num(b[s.key]);
        }
      });
      return sb - sa;
    });
    return {
      labels: sorted.map((r) => {
        const v = r[labelKey];
        return v === null || v === undefined ? "(unset)" : String(v);
      }),
      datasets: series.map((s, i) => ({
        label: s.label,
        data: sorted.map((r) => num(r[s.key])),
        backgroundColor: s.color,
        hidden: !visibleSet.has(i),
      })),
    };
  };

  const initial = rebuild(visible);

  // SGNL-130: Chart is a bundled ESM import — cast to the local ChartGlobal
  // interface so the constructor call is typed. ARCH-02 on PR #90.
  const ChartCtor = Chart as unknown as ChartGlobal;

  // SGNL-109 — Bar orientation parameterised. `indexAxis` ("y" default,
  // legacy BODQ22026 horizontal layout; "x" = vertical bars for the AI
  // breakdown ranking view). Tooltip + scales adapt to whichever axis
  // carries money so $K/$M ticks render on the right scale.
  // Chart.js tick callbacks pass numeric values as strings on some scales —
  // coerce before formatting so $K/$M abbreviation always applies; only
  // fall back to the raw string when coercion truly fails (non-numeric tick).
  const moneyTicks = {
    callback: (v: number | string) => {
      const n = typeof v === "string" ? Number(v) : v;
      return Number.isFinite(n) ? fmtMoney(n) : String(v);
    },
  };
  const chart = new ChartCtor(ctx, {
    type: "bar",
    data: initial,
    options: {
      indexAxis,
      responsive: true,
      maintainAspectRatio: true,
      plugins: {
        title: { display: true, text: title },
        legend: {
          position: "top",
          onClick(_e: unknown, item: { datasetIndex: number }): void {
            const idx = item.datasetIndex;
            if (visible.has(idx)) visible.delete(idx);
            else visible.add(idx);
            const fresh = rebuild(visible);
            chart.data.labels = fresh.labels;
            chart.data.datasets.forEach((ds, i) => {
              ds.data = fresh.datasets[i].data;
              ds.hidden = fresh.datasets[i].hidden;
            });
            chart.update();
          },
        },
        tooltip: {
          mode: "index",
          intersect: false,
          callbacks: {
            label: (c: { dataset: { label: string }; parsed: Record<"x" | "y", number> }) =>
              `${c.dataset.label}: ${fmtMoney(c.parsed[moneyAxis])}`,
            footer: (items: { parsed: Record<"x" | "y", number> }[]) =>
              `Total: ${fmtMoney(items.reduce((s, it) => s + (it.parsed[moneyAxis] || 0), 0))}`,
          },
        },
      },
      scales: {
        x: { stacked: true, ...(moneyAxis === "x" ? { ticks: moneyTicks } : {}) },
        y: { stacked: true, ...(moneyAxis === "y" ? { ticks: moneyTicks } : {}) },
      },
    },
  });
  chartRegistry.set(canvasId, chart);
}

// Re-export so existing call sites that import fmtMoney from charts.ts keep
// working. New code should import directly from "../lib/format".
export { fmtMoney };

// ---------------------------------------------------------------------------
// SGNL-72 — Sentiment trajectory scatter chart
// ---------------------------------------------------------------------------

export interface SentimentPoint {
  date: string;   // ISO datetime string — used as X axis label
  score: number;  // -1..+1
  direction: "inbound" | "outbound" | "internal";
  label: string;  // tooltip: subject or "(no subject)"
  reason: string | null;
}

const _DIRECTION_COLOR: Record<string, string> = {
  inbound: "#4a90d9",   // blue — customer voice
  outbound: "#7b61ff",  // indigo — rep voice
  internal: "#9e9e9e",  // grey — internal/unknown
};

/**
 * Render a sentiment trajectory scatter chart in `canvasId`.
 * Each point is one scored message; colour encodes direction.
 * A horizontal zero-line anchors the "neutral" baseline.
 * Returns a destroy handle (call on re-render to avoid canvas reuse errors).
 */
export function drawSentimentChart(
  canvasId: string,
  points: SentimentPoint[],
): () => void {
  const prev = chartRegistry.get(canvasId);
  if (prev) prev.destroy();

  const canvas = document.getElementById(canvasId) as HTMLCanvasElement | null;
  if (!canvas) return () => {};

  // Group points into three datasets by direction so each gets its own
  // legend entry + colour. Chart.js scatter requires {x, y} data shape.
  const groups = {
    inbound: [] as { x: string; y: number; label: string; reason: string | null }[],
    outbound: [] as { x: string; y: number; label: string; reason: string | null }[],
    internal: [] as { x: string; y: number; label: string; reason: string | null }[],
  };
  for (const p of points) {
    const dir = p.direction in groups ? p.direction as keyof typeof groups : "internal";
    groups[dir].push({ x: p.date.slice(0, 10), y: p.score, label: p.label, reason: p.reason });
  }

  const datasets = (
    [
      { key: "inbound" as const, label: "Inbound (customer)" },
      { key: "outbound" as const, label: "Outbound (rep)" },
      { key: "internal" as const, label: "Internal" },
    ] as const
  )
    .filter(({ key }) => groups[key].length > 0)
    .map(({ key, label }) => ({
      label,
      data: groups[key],
      backgroundColor: _DIRECTION_COLOR[key],
      borderColor: _DIRECTION_COLOR[key],
      pointRadius: 5,
      pointHoverRadius: 7,
      showLine: false,
    }));

  // All unique dates sorted for the X axis.
  const allDates = [...new Set(points.map((p) => p.date.slice(0, 10)))].sort();

  const ChartCtor = Chart as unknown as ChartGlobal;
  const chart = new ChartCtor(canvas, {
    type: "scatter",
    data: { labels: allDates, datasets },
    options: {
      responsive: true,
      maintainAspectRatio: false,
      plugins: {
        legend: { position: "top" },
        tooltip: {
          callbacks: {
            label: (ctx: { raw: { label: string; y: number; reason: string | null } }) => {
              const { label, y, reason } = ctx.raw;
              const score = y.toFixed(2);
              const base = `${label}: ${score}`;
              return reason ? [`${base}`, `  → ${reason}`] : [base];
            },
          },
        },
        // annotation plugin not registered — intentionally omitted
      },
      scales: {
        x: {
          type: "category",
          labels: allDates,
          title: { display: true, text: "Date" },
        },
        y: {
          min: -1,
          max: 1,
          title: { display: true, text: "Sentiment" },
          ticks: {
            callback: (v: number | string) => {
              const n = Number(v);
              if (n === 1) return "+1 positive";
              if (n === 0) return "0 neutral";
              if (n === -1) return "−1 negative";
              return String(n);
            },
          },
        },
      },
    },
  });

  chartRegistry.set(canvasId, chart as ChartHandle);
  return () => {
    chart.destroy();
    chartRegistry.delete(canvasId);
  };
}
